import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ArrowRight } from "lucide-react";

interface Step2RequirementsProps {
  requirements: string[];
  selectedRequirements: string[];
  onSelectionChange: (selected: string[]) => void;
  onNext: () => void;
  onBack: () => void;
}

export const Step2Requirements: React.FC<Step2RequirementsProps> = ({
  requirements,
  selectedRequirements,
  onSelectionChange,
  onNext,
  onBack
}) => {
  const handleToggleRequirement = (requirement: string) => {
    if (selectedRequirements.includes(requirement)) {
      onSelectionChange(selectedRequirements.filter(r => r !== requirement));
    } else {
      onSelectionChange([...selectedRequirements, requirement]);
    }
  };

  const handleSelectAll = () => {
    onSelectionChange(requirements);
  };

  const handleDeselectAll = () => {
    onSelectionChange([]);
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader className="space-y-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-semibold">
            Advanced Tailoring - Step 2 of 3
          </CardTitle>
          <Badge variant="secondary" className="px-3 py-1">
            Requirements
          </Badge>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground">
            Select the requirements you meet for this position
          </p>
          <div className="text-sm text-muted-foreground">
            {selectedRequirements.length} of {requirements.length} selected
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Selection Controls */}
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleSelectAll}
          >
            Select All
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleDeselectAll}
          >
            Deselect All
          </Button>
        </div>

        {/* Requirements List */}
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {requirements.map((requirement, index) => (
            <div
              key={index}
              className="flex items-start gap-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer transition-colors"
              onClick={() => handleToggleRequirement(requirement)}
            >
              <Checkbox
                checked={selectedRequirements.includes(requirement)}
                onChange={() => handleToggleRequirement(requirement)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-primary">
                    #{index + 1}
                  </span>
                  <Badge 
                    variant={index < 3 ? "default" : index < 7 ? "secondary" : "outline"}
                    className="text-xs"
                  >
                    {index < 3 ? "Critical" : index < 7 ? "Important" : "Preferred"}
                  </Badge>
                </div>
                <p className="text-sm leading-relaxed">{requirement}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Responsibilities
          </Button>
          <Button 
            onClick={onNext}
            disabled={selectedRequirements.length === 0}
          >
            Continue to Skills
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};